'use client';

import * as React from 'react';

import { cn } from '../../utils';

/**
 * Pinned header cell. `sticky` has to sit on `th` rather than `thead`, and the table has to opt out
 * of `border-collapse` (see `TABLE_STICKY_LAYOUT_CLASS`) — collapsed borders disable sticky on table
 * sections. The background must stay opaque or body rows show through the pinned row while scrolling.
 */
export const TABLE_STICKY_HEADER_CELL_CLASS = 'sticky top-0 z-20 bg-background';

/** Header cell also frozen to the left edge; outranks both the header row and sticky body cells. */
export const TABLE_STICKY_FROZEN_HEADER_CELL_CLASS = 'sticky top-0 left-0 z-30 bg-background';

/** Body cell frozen to the left edge. Below the header plane, above normal cells. */
export const TABLE_STICKY_FROZEN_CELL_CLASS = 'sticky left-0 z-10 bg-background';

/** Tailwind preflight sets `border-collapse: collapse`, which breaks sticky. */
const TABLE_STICKY_LAYOUT_CLASS = 'border-separate border-spacing-0';

/**
 * Scrollport a pinned header needs to pin against. A `max-height` rather than a height, so short
 * tables stop after their last row instead of reserving empty space, and the result is identical on
 * every render — nothing here is measured.
 */
export const TABLE_SCROLLPORT_CLASS = 'max-h-[min(900px,calc(100dvh-12rem))] overflow-auto';

/**
 * Lets `TableHead` know whether to pin itself. Passed by context rather than a descendant selector
 * (`[&_thead_th]:…`) so callers can still override the background or z-index with a plain class —
 * a descendant selector outranks them and silently wins.
 */
const StickyHeaderContext = React.createContext(false);

interface TableProps extends React.ComponentProps<'table'> {
    noWrapper?: boolean;
    /** Pin header cells to the top of the nearest scrollport. */
    stickyHeader?: boolean;
    /**
     * Make the wrapper the scrollport itself. For tables with no surrounding element to put
     * `TABLE_SCROLLPORT_CLASS` on — otherwise prefer that class on the existing wrapper, so the
     * scroll area and its border/padding stay on one element.
     */
    scrollport?: boolean;
}

function Table({ className, noWrapper, stickyHeader = true, scrollport, ...tableProps }: TableProps) {
    const table = (
        <table data-slot="table" className={cn('w-full caption-bottom text-sm', stickyHeader && TABLE_STICKY_LAYOUT_CLASS, className)} {...tableProps} />
    );

    return (
        <StickyHeaderContext.Provider value={stickyHeader}>
            {noWrapper ? (
                table
            ) : (
                // A pinned header pins to the nearest scroll container, and `overflow-x-auto` makes
                // this div one on both axes — it then captures the pin and never scrolls vertically.
                // So unless asked to be the scrollport, step aside and let the caller's own it.
                <div
                    data-slot="table-container"
                    className={cn('relative w-full', scrollport ? TABLE_SCROLLPORT_CLASS : stickyHeader ? 'overflow-visible' : 'overflow-x-auto')}
                >
                    {table}
                </div>
            )}
        </StickyHeaderContext.Provider>
    );
}

function TableHeader({ className, ...props }: React.ComponentProps<'thead'>) {
    return <thead data-slot="table-header" className={cn(className)} {...props} />;
}

function TableBody({ className, ...props }: React.ComponentProps<'tbody'>) {
    return <tbody data-slot="table-body" className={cn(className)} {...props} />;
}

function TableFooter({ className, ...props }: React.ComponentProps<'tfoot'>) {
    return (
        <tfoot
            data-slot="table-footer"
            className={cn('border-t bg-muted/50 font-medium [&>tr:last-child>td]:border-b-0 [&>tr:last-child>th]:border-b-0', className)}
            {...props}
        />
    );
}

function TableRow({ className, ...props }: React.ComponentProps<'tr'>) {
    return <tr data-slot="table-row" className={cn('min-h-[44px] transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted', className)} {...props} />;
}

function TableHead({ className, ...props }: React.ComponentProps<'th'>) {
    const stickyHeader = React.useContext(StickyHeaderContext);

    return (
        <th
            data-slot="table-head"
            className={cn(
                'h-10 whitespace-nowrap border-b border-primary px-4 py-3 text-left align-middle font-medium text-sm text-foreground [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
                stickyHeader && TABLE_STICKY_HEADER_CELL_CLASS,
                className,
            )}
            {...props}
        />
    );
}

function TableCell({ className, ...props }: React.ComponentProps<'td'>) {
    return (
        <td
            data-slot="table-cell"
            className={cn(
                'whitespace-nowrap border-b border-border px-4 py-3 text-sm align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
                className,
            )}
            {...props}
        />
    );
}

function TableCaption({ className, ...props }: React.ComponentProps<'caption'>) {
    return <caption data-slot="table-caption" className={cn('mt-4 text-muted-foreground text-sm', className)} {...props} />;
}

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption };
